import { createSlice } from '@reduxjs/toolkit';
import { getSession } from 'next-auth/react';
import {
  DocumentStatus,
  EditPatientGuardiansInput,
  MatchResult,
  PatientEmergencyContactInput,
  PatientInput,
  PatientPharmacyInput,
  PatientPrimaryPolicyholderInput,
  SectionName,
  TerminationReason,
} from '../generated/graphql';
import {
  setAppointmentTime,
  setMatches,
  setPatientShouldNotBeMatchedReasons,
  setProvider,
  setZdProviderMatchFailureReasons,
  setZdProviderTreatmentDealbreakerReasons,
  setZocdocProvider,
} from './matching';
import { setHasPreviouslyCompletedAssessment, updateMeta } from './meta';
import {
  editEmergencyContact,
  editPatientGuardians,
  editPatientPharmacy,
  editPrimaryPolicyholder,
  fetchMatches,
  getDocumentStatus,
  updateAssessmentAnswers,
  updateDocument,
  updateGoogleClickId,
  updatePatientConsentReferrerSharing,
  updatePatientDemographics,
  zdCheckProviderMatch,
} from './api-wrappers';
import {
  setDocumentId,
  setReferralSource,
  setSessionPatientId,
  setShortCode,
  setUser,
} from './session';
import {
  clearInsuranceResponses,
  formatAnswersForBackend,
  formatAnswersForState,
  getAssessmentSection,
} from './helpers/assessment';
import {
  AppDispatch,
  AssessmentState,
  DispatchUpdateAssessmentInput,
  GetState,
  GuardianDemographicsState,
  ReferralSource,
  RootState,
  Screens,
  Thunk,
} from './types';
import {
  getEventLabelFromMeta,
  trackFetchingMatches,
  trackSuccessfulCompletedAssessment,
} from '../helpers/analytics';
import { MinorPolicyHolderRelationLabelValueMap } from '../helpers/assessment';
import { sendTermination } from './meta-config/targets';
import { trackConversion } from '../experimentation/client';
import { KameleeonGoal } from '../experimentation/base';

export const initialAssessmentState: AssessmentState = {
  patient: {},
  guardians: undefined,
  medicalInfoAnswers: {},
  reasonVisitAnswers: {},
};

export const assessmentState = createSlice({
  name: 'assessment',
  initialState: initialAssessmentState,
  reducers: {
    setPatient: (state, action) => {
      state.patient = { ...state.patient, ...action.payload };
    },
    setGuardians: (state, action) => {
      state.guardians = { ...state.guardians, ...action.payload };
    },
    setReasonVisitAnswers: (state, action) => {
      state.reasonVisitAnswers = {
        ...state.reasonVisitAnswers,
        ...action.payload,
      };
    },
    setMedicalInfoAnswers: (state, action) => {
      state.medicalInfoAnswers = {
        ...state.medicalInfoAnswers,
        ...action.payload,
      };
    },
    removePolicyholderDetails: state => {
      const { MI_PrimaryPolicyHolderDetails, ...newMedicalInfoAnswers } =
        state.medicalInfoAnswers;
      state.medicalInfoAnswers = newMedicalInfoAnswers;
    },
    removePharmacyDetails: state => {
      const { MI_PrimaryPharmacyDetails, ...newMedicalInfoAnswers } =
        state.medicalInfoAnswers;
      state.medicalInfoAnswers = newMedicalInfoAnswers;
    },
    removeMedicationList: state => {
      const { MI_MedicationList, ...newMedicalInfoAnswers } =
        state.medicalInfoAnswers;
      state.medicalInfoAnswers = newMedicalInfoAnswers;
    },
    resetAssessmentState: (state, action) => {
      Object.assign(state, action.payload);
    },
    clearPlanAndId: state => {
      const { MI_InsurancePlan, MI_InsuranceId, ...newMedicalInfoAnswers } =
        state.medicalInfoAnswers;
      state.medicalInfoAnswers = newMedicalInfoAnswers;
    },
  },
});

export const {
  setPatient,
  setGuardians,
  setReasonVisitAnswers,
  setMedicalInfoAnswers,
  removePolicyholderDetails,
  removePharmacyDetails,
  removeMedicationList,
  resetAssessmentState,
  clearPlanAndId,
} = assessmentState.actions;

export const selectPatient = (state: RootState) => state.assessment.patient;

export const selectPatientGuardians = (state: RootState) =>
  state.assessment.guardians;

export const selectReasonVisitAnswers = (state: RootState) =>
  state.assessment.reasonVisitAnswers;

export const selectMedicalInfoAnswers = (state: RootState) =>
  state.assessment.medicalInfoAnswers;

export const selectPolicyholderDetails = (state: RootState) =>
  state.assessment.medicalInfoAnswers.MI_PrimaryPolicyHolderDetails;

export const selectPharmacyDetails = (state: RootState) =>
  state.assessment.medicalInfoAnswers.MI_PrimaryPharmacyDetails;

export const selectMedicationList = (state: RootState) =>
  state.assessment.medicalInfoAnswers.MI_MedicationList;

export const updateLandingRouter = (): Thunk => async dispatch => {
  // update user and hasPreviouslyCompletedAssessment, if applicable
  const res = await getSession();
  if (res?.user) {
    dispatch(setUser(res?.user));

    const { data, errors } = await getDocumentStatus();
    if (errors) return errors;
    const hasPreviouslyCompletedAssessment = Boolean(
      data?.getMe?.document?.status
    );
    dispatch(
      setHasPreviouslyCompletedAssessment(hasPreviouslyCompletedAssessment)
    );
    if (data?.getMe?.referralSource) {
      const { referralSource } = data.getMe;
      dispatch(setReferralSource(referralSource));
      dispatch(
        assessmentState.actions.setPatient({
          firstName: data.getMe.firstName,
          lastName: data.getMe.lastName,
          birthDate: data.getMe.birthDate,
        })
      );
      if (referralSource === ReferralSource.Zocdoc) {
        dispatch(setZocdocProvider(data.getMe?.zocdocAppointment?.provider));
      }
    }
  }
  dispatch(updateMeta());
};

export const updateDemographics =
  (patient: PatientInput): Thunk =>
  async (dispatch, getState) => {
    const { session } = getState();
    if (!session.documentId) {
      const { errors, data } = await updateDocument({});
      if (errors) return errors;
      dispatch(setDocumentId(data.updateDocument.id));
    }

    const { errors, data } = await updatePatientDemographics(patient);

    if (errors) return errors;
    const { id, shortCode } = data.updatePatientDemographics;
    dispatch(setSessionPatientId(id));
    dispatch(setShortCode(shortCode));
    dispatch(setPatient(patient));
    dispatch(updateMeta());
  };

export const updateConsentReferrerSharing =
  (consentReferrerSharing: boolean): Thunk =>
  async dispatch => {
    const { errors } = await updatePatientConsentReferrerSharing(
      consentReferrerSharing
    );
    if (errors) return errors;

    dispatch(updateMeta());
  };

export const updateEmergencyContact =
  (eC: PatientEmergencyContactInput): Thunk =>
  async (dispatch, getState) => {
    const { sessionPatientId } = getState().session;
    const { errors } = await editEmergencyContact({
      sessionPatientId,
      ...eC,
    });

    if (errors) return errors;

    dispatch(setMedicalInfoAnswers({ [Screens.MI_EmergencyContact]: eC }));
    dispatch(updateMeta());
  };

export const updateAssessment =
  (answers: DispatchUpdateAssessmentInput, shouldChangeScreen = true): Thunk =>
  async dispatch => {
    const section = getAssessmentSection(answers[0].questionCode);
    const answersForBackend = formatAnswersForBackend(answers);
    const updateAssessmentAnswersInput = {
      section,
      answers: answersForBackend,
    };
    const { errors } = await updateAssessmentAnswers(
      updateAssessmentAnswersInput
    );

    if (errors) return errors;

    const answersForState = formatAnswersForState(answers);
    if (section === SectionName.ReasonVisit) {
      dispatch(setReasonVisitAnswers(answersForState));
    } else if (section === SectionName.MedicalInfo) {
      dispatch(setMedicalInfoAnswers(answersForState));
    }
    if (shouldChangeScreen) {
      dispatch(updateMeta());
    }
  };

export const updateInsuranceForm =
  (
    insuranceInput: DispatchUpdateAssessmentInput,
    hasOONProvider: boolean,
    shouldChangeScreen?: boolean
  ): Thunk =>
  async (dispatch: AppDispatch, getState: GetState) => {
    let errors;
    errors = await clearInsuranceResponses();
    if (errors) return errors;
    dispatch(clearPlanAndId());

    errors = await dispatch(
      updateAssessment(insuranceInput, shouldChangeScreen)
    );
    if (errors) return errors;
    await trackConversion(KameleeonGoal.InsuranceInformationSaved);

    if (hasOONProvider) {
      const state = getState();
      sendTermination(TerminationReason.PayorOutOfNetwork, state);
    }
  };

export const updatePrimaryPolicyholder =
  (
    isNotPolicyholder: Boolean,
    policyholderDetails: PatientPrimaryPolicyholderInput,
    insuranceUserInput?: DispatchUpdateAssessmentInput
  ): Thunk =>
  async (dispatch, getState) => {
    const currentPolicyholderDetails = selectPolicyholderDetails(getState());

    if (isNotPolicyholder) {
      const { errors } = await editPrimaryPolicyholder(policyholderDetails);

      if (errors) return errors;
      const payload = { MI_PrimaryPolicyHolderDetails: policyholderDetails };
      dispatch(setMedicalInfoAnswers(payload));
    }
    if (!isNotPolicyholder && currentPolicyholderDetails) {
      const { errors } = await editPrimaryPolicyholder();

      if (errors) return errors;
      dispatch(removePolicyholderDetails());
    }

    const isPolicyholderAnswer = [
      {
        questionCode: 'MI_PrimaryPolicyHolder',
        answerValue: isNotPolicyholder ? ['No'] : ['Yes'],
      },
      ...(insuranceUserInput || []),
    ];
    const errors = await dispatch(updateAssessment(isPolicyholderAnswer));

    if (errors) return errors;
  };

export const updateGuardians =
  (input: EditPatientGuardiansInput): Thunk =>
  async (dispatch, getState) => {
    const { errors } = await editPatientGuardians(input);

    if (errors) return errors;

    const removedPrimaryPolicyholder =
      !input.secondary &&
      selectPolicyholderDetails(getState())?.relationToPatient ===
        MinorPolicyHolderRelationLabelValueMap['Secondary guardian'];
    if (removedPrimaryPolicyholder) dispatch(removePolicyholderDetails());

    dispatch(setGuardians(input as GuardianDemographicsState));
    dispatch(updateMeta());
  };

export const updateMedicationAndPharmacy =
  (
    currentlyTakesMeds: Boolean,
    medications: string[],
    openToMedication: Boolean,
    hasPharmacy: Boolean,
    pharmacyDetails: PatientPharmacyInput
  ): Thunk =>
  async (dispatch, getState) => {
    const currentMedicationList = selectMedicationList(getState());
    const currentPharmacyDetails = selectPharmacyDetails(getState());

    if (!currentlyTakesMeds && currentMedicationList) {
      dispatch(removeMedicationList());
    }

    if (hasPharmacy) {
      const { errors } = await editPatientPharmacy(pharmacyDetails);

      if (errors) return errors;
      const payload = { MI_PrimaryPharmacyDetails: pharmacyDetails };
      dispatch(setMedicalInfoAnswers(payload));
    }

    if (!hasPharmacy && currentPharmacyDetails) {
      const { errors } = await editPatientPharmacy(null);
      if (errors) return errors;

      dispatch(removePharmacyDetails());
    }

    const medicationAndPharmacyAnswers = [
      {
        questionCode: 'MI_PrescriptionMedication',
        answerValue: currentlyTakesMeds ? ['Yes'] : ['No'],
      },
      {
        questionCode: 'MI_OpenPsychiatricMeds',
        answerValue: openToMedication ? ['Yes'] : ['No'],
      },
      {
        questionCode: 'MI_PrimaryPharmacy',
        answerValue: hasPharmacy ? ['Yes'] : ['No'],
      },
    ];

    if (currentlyTakesMeds) {
      medicationAndPharmacyAnswers.push({
        questionCode: 'MI_MedicationList',
        answerValue: medications,
      });
    }

    const errors = await dispatch(
      updateAssessment(medicationAndPharmacyAnswers)
    );

    if (errors) return errors;
  };

const loadMatches = async (dispatch, session) => {
  trackFetchingMatches();
  const { errors, data } = await fetchMatches(session.sessionPatientId);
  if (errors) return errors;

  const { patientMatchFailureReasons, providers } = data.getProviderMatches;
  if (patientMatchFailureReasons.length === 0) {
    const matches = providers.map((m: MatchResult) => m.provider);
    dispatch(setMatches(matches));
  }
};

const loadZocdocMatches = async dispatch => {
  trackFetchingMatches();
  const { data, errors } = await zdCheckProviderMatch();
  if (errors) return errors;
  const {
    patientMatchFailureReasons,
    matchedProviders,
    zocdocProvider,
    zdProviderDealbreakersForPatientReasons,
    zdProviderMatchFailureReasons,
    isZDProviderAMatch,
    zocdocAppointment,
  } = data.checkZDProviderForMatch;
  if (isZDProviderAMatch) {
    dispatch(setAppointmentTime(zocdocAppointment?.startTime));
    dispatch(setProvider(zocdocProvider));
  } else {
    dispatch(setPatientShouldNotBeMatchedReasons(patientMatchFailureReasons));
    dispatch(setZdProviderMatchFailureReasons(zdProviderMatchFailureReasons));
    dispatch(
      setZdProviderTreatmentDealbreakerReasons(
        zdProviderDealbreakersForPatientReasons
      )
    );
    dispatch(setProvider(zocdocProvider));
    if (!patientMatchFailureReasons?.length) {
      const matches = matchedProviders.map((m: MatchResult) => m.provider);
      dispatch(setMatches(matches));
    }
  }
};

export const submitAssessment =
  (googleClickId?: string): Thunk =>
  async (dispatch, getState) => {
    const { session, assessment, meta } = getState();
    const eventLabel = getEventLabelFromMeta(meta.present);
    trackSuccessfulCompletedAssessment(eventLabel);

    const documentInput = { status: DocumentStatus.Completed };
    const { errors: documentErrors } = await updateDocument(documentInput);
    if (documentErrors) return documentErrors;

    if (googleClickId) await updateGoogleClickId(googleClickId);

    const { MI_HaveAppointment } = assessment.medicalInfoAnswers;

    if (session.referralSource === ReferralSource.Zocdoc) {
      await loadZocdocMatches(dispatch);
    } else if (MI_HaveAppointment.includes('No')) {
      await loadMatches(dispatch, session);
    }

    dispatch(updateMeta());
  };

export default assessmentState.reducer;
